import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { Subject, Subscription, forkJoin, Observable, of } from "rxjs";
import { DigitalCardService } from "../digital-card.service";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { EmployeesService } from "../../employees/employees.service";
import { EmployeePickerItem } from "../../employees/employees.interface";
import { SettingsService } from "../../settings/settings.service";
import type {
  CompanySettings,
  EmployeeSetting,
} from "../../settings/settings.interface";
import {
  CardFieldToggle,
  DigitalCardSettingsData,
  EmployeeWithBrand,
  DEFAULT_WALLET_QR_CAPTION,
  type WalletGooglePrimaryImage,
  type WalletGoogleRightColumn,
  type WalletLogoMode,
  type WalletSettingsData,
  type CardPreviewSnapshot,
} from "../card-digital.interface";
import {
  buildCardFieldToggles,
  customLinkFieldKey,
  isCustomLinkFieldKey,
} from "../digital-card-fields";
import { GetDigitalCardProfilesAction } from "../digital-cards.actions";
import {
  generateEmployeeHash,
  getDigitalCardBaseUrl,
} from "../digital-card-link.service";
import { EditorComponent } from "../../shared/editor/editor.component";
import { ImagesService } from "../../media/images.service";
import type { Image as MediaImage } from "../../media/media.interface";
import {
  ALLOWED_RASTER_IMAGE_MIME_TYPES,
  type Image,
} from "../../shared/image-uploader/image.interface";
import { BranchSettingsService } from "../../branch-settings/branch-settings.service";
import type { Branch } from "../../branch-settings/branch-settings.interface";
import {
  openFreezeFrameSource,
  openFreezeFrameSourceFromUrl,
} from "../gif-freeze-frame";
import type { FreezeFrameSource } from "../gif-freeze-frame.interface";
import type {
  DigitalCardFontPreset,
  DigitalCardFormControls,
  WalletDraftFormControls,
} from "../card-digital.interface";
import { CompanyService } from "../../company/company.service";
import {
  PROFILE_LANGUAGE_LABEL_KEYS,
  PROFILE_LANGUAGES,
  type ProfileLanguage,
} from "../../shared/profile-language.interface";
import { AuthService } from "../../auth/auth.service";
import type { OptionalInterfaces } from "../../client/client.interface";
import {
  BRAND_FONT_PRESET_ID,
  BRAND_UI_FONT_PRESETS,
} from "../../shared/font-presets";

@Component({
  selector: "app-digital-card-editor",
  templateUrl: "./digital-card-editor.component.html",
  styleUrls: ["./digital-card-editor.component.scss"],
  standalone: false,
})
export class DigitalCardEditorComponent implements OnInit, OnDestroy {
  readonly walletLogoSelInherit = "__wallet_logo_inherit__";
  readonly rasterMimeTypes = ALLOWED_RASTER_IMAGE_MIME_TYPES;

  @ViewChild("customHtmlEditor") customHtmlEditor: EditorComponent | null =
    null;
  profileId: string | null = null;
  isCreateMode = false;
  loading = true;
  previewSettled = false;
  previewEmployeeLoading = false;
  saving = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  bannerPreviewUrl: string | undefined;
  bannerPosterPreviewUrl: string | undefined;
  bannerIsAnimated = false;
  freezeFrameLoading = false;
  freezeFrameCount = 0;
  freezeFrameIndex = 0;
  freezeFramePreviewUrl: string | undefined;
  private freezeFrameSource: FreezeFrameSource | null = null;
  private freezeFrameToken = 0;
  private readonly freezeFramePosterUpload$ = new Subject<number>();
  goToCardUrl: string | null = null;
  private profileEmail: string | null = null;
  profileEmployee: EmployeeWithBrand | null = null;
  previewEmployee: EmployeePickerItem | null = null;
  previewEmployeeData: EmployeeWithBrand | null = null;
  previewQrData = "";
  editorTabIndex = 0;
  fieldToggles: CardFieldToggle[] = [];
  orderedFieldToggles: CardFieldToggle[] = [];
  previewSnapshot: CardPreviewSnapshot = {
    settings: {},
    walletSettings: {},
    employee: null,
  };
  private profileStatus: string | undefined;
  private profileCurrentRenderId: string | null = null;
  private readonly subscriptions = new Subscription();
  private readonly previewEmail$ = new Subject<string>();
  mediaImages: MediaImage[] = [];
  mediaImagesLoading = false;
  readonly mediaLabel = (image: MediaImage) => image.name || "";
  readonly mediaValue = (image: MediaImage) => image.imageUrl;
  readonly mediaImage = (image: MediaImage) => image.imageUrl;
  private readonly builtInFontPresets = BRAND_UI_FONT_PRESETS;
  private companyFontFamily = "";
  private companyFontFileUrl: string | null = null;
  enabledProfileLanguages: ProfileLanguage[] = [...PROFILE_LANGUAGES];
  readonly profileLanguageLabelKeys = PROFILE_LANGUAGE_LABEL_KEYS;

  form = new FormGroup<DigitalCardFormControls>({
    displayName: new FormControl<string>("", {
      nonNullable: true,
    }),
    fontPreset: new FormControl<string>("default", { nonNullable: true }),
    bannerType: new FormControl<"gradient" | "image">("gradient"),
    bannerGradientStart: new FormControl<string>("#ffffff"),
    bannerGradientEnd: new FormControl<string>("#f5f5f0"),
    brandColorPrimary: new FormControl<string>("#000000"),
    brandColorSecondary: new FormControl<string>(""),
    cardBackgroundColor: new FormControl<string>("#ffffff"),
    pageBackgroundColor: new FormControl<string>("#ffffff"),
    showBanner: new FormControl<boolean>(true),
    showAvatar: new FormControl<boolean>(true),
    showJobTitle: new FormControl<boolean>(true),
    showQr: new FormControl<boolean>(true),
    showCompanyLogo: new FormControl<boolean>(true),
    showAboutMe: new FormControl<boolean>(true),
    showPronounceName: new FormControl<boolean>(true),
    showSaveContact: new FormControl<boolean>(true),
    showBookMeeting: new FormControl<boolean>(true),
    visibleFields: new FormControl<Record<string, boolean>>(
      {},
      { nonNullable: true },
    ),
    fieldOrder: new FormControl<string[]>([], { nonNullable: true }),
    companyLogoUrl: new FormControl<string>("", { nonNullable: true }),
    customHtml: new FormControl<string>("", { nonNullable: true }),
    profileLanguage: new FormControl<string>("", { nonNullable: true }),
    walletDraft: new FormGroup<WalletDraftFormControls>({
      inheritWalletBackgroundFromCard: new FormControl<boolean>(true, {
        nonNullable: true,
      }),
      logoMode: new FormControl<WalletLogoMode>("inherit", {
        nonNullable: true,
      }),
      logoCustomUrl: new FormControl<string>("", { nonNullable: true }),
      appleBackgroundHex: new FormControl<string>("", { nonNullable: true }),
      googleBackgroundHex: new FormControl<string>("", { nonNullable: true }),
      inheritCardFieldVisibility: new FormControl<boolean>(true, {
        nonNullable: true,
      }),
      walletShowAvatar: new FormControl<boolean>(true, { nonNullable: true }),
      walletShowJobTitle: new FormControl<boolean>(true, { nonNullable: true }),
      walletShowEmail: new FormControl<boolean>(true, { nonNullable: true }),
      walletShowPhone: new FormControl<boolean>(true, { nonNullable: true }),
      walletShowPassCompanyName: new FormControl<boolean>(true, {
        nonNullable: true,
      }),
      walletQrCaption: new FormControl<string>(DEFAULT_WALLET_QR_CAPTION, {
        nonNullable: true,
      }),
      googlePrimaryImage: new FormControl<WalletGooglePrimaryImage>(
        "employee_photo",
        {
          nonNullable: true,
        },
      ),
      googleRightColumn: new FormControl<WalletGoogleRightColumn>("auto", {
        nonNullable: true,
      }),
    }),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private digitalCardService: DigitalCardService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private imagesService: ImagesService,
    private employeesService: EmployeesService,
    private settingsService: SettingsService,
    private branchSettingsService: BranchSettingsService,
    private companyService: CompanyService,
    private auth: AuthService,
    private dialog: MatDialog,
  ) {}

  get optionalInterfaces(): OptionalInterfaces | null {
    return this.auth.appUser?.optionalInterfaces ?? null;
  }

  get namePronunciationEnabled(): boolean {
    return !!this.optionalInterfaces?.namePronunciation;
  }

  get walletTabEnabled(): boolean {
    return (
      !!this.optionalInterfaces?.appleWallet ||
      !!this.optionalInterfaces?.googleWallet
    );
  }

  get appleWalletEnabled(): boolean {
    return !!this.optionalInterfaces?.appleWallet;
  }

  get googleWalletEnabled(): boolean {
    return !!this.optionalInterfaces?.googleWallet;
  }

  get fontPresets(): DigitalCardFontPreset[] {
    if (!this.companyBrandFontReady) {
      return this.builtInFontPresets;
    }
    return [
      {
        id: BRAND_FONT_PRESET_ID,
        label: this.companyFontFamily,
        fontFamily: `"${this.companyFontFamily.replace(/"/g, "")}", sans-serif`,
      },
      ...this.builtInFontPresets,
    ];
  }

  get companyBrandFontReady(): boolean {
    return (
      this.companyFontFamily.trim().length > 0 &&
      (this.companyFontFileUrl?.trim().length ?? 0) > 0
    );
  }

  ngOnInit(): void {
    if (!this.namePronunciationEnabled) {
      this.form.patchValue({ showPronounceName: false });
    }
    if (!this.walletTabEnabled && this.editorTabIndex !== 0) {
      this.editorTabIndex = 0;
    }
    this.loadMediaImages();
    this.initPreviewEmployeeStream();
    this.bootstrapPreviewEmployee();
    this.subscriptions.add(
      this.freezeFramePosterUpload$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((index) => {
          void this.uploadFreezeFramePoster(index);
        }),
    );
    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        this.syncDerivedEditorState();
      }),
    );
    this.subscriptions.add(
      this.form.controls.bannerType.valueChanges.subscribe((type) => {
        if (type !== "image") {
          this.clearFreezeFrames();
          this.bannerPosterPreviewUrl = undefined;
          this.bannerIsAnimated = false;
          this.syncDerivedEditorState();
        }
      }),
    );
    const profileIdParam = this.route.snapshot.paramMap.get("profileId");
    const company$: Observable<
      Pick<
        CompanySettings,
        "brandFontFamily" | "brandFontFileUrl" | "brandFontFileName"
      >
    > = this.companyService.get().pipe(
      map((company) => ({
        brandFontFamily: company.brandFontFamily ?? null,
        brandFontFileUrl: company.brandFontFileUrl ?? null,
        brandFontFileName: company.brandFontFileName ?? null,
      })),
      catchError(() =>
        of({
          brandFontFamily: null,
          brandFontFileUrl: null,
          brandFontFileName: null,
        }),
      ),
    );
    const fieldToggles$ = forkJoin({
      variables: this.settingsService
        .getEmployeeSettings()
        .pipe(catchError(() => of([]))),
      branches: this.branchSettingsService
        .fetchBranches()
        .pipe(catchError(() => of([]))),
    }).pipe(
      map(({ variables, branches }) =>
        buildCardFieldToggles(
          [...variables, ...this.branchVariablesToEmployeeSettings(branches)],
          this.translateService,
        ),
      ),
    );
    if (profileIdParam && profileIdParam !== "create") {
      this.profileId = profileIdParam;
      this.isCreateMode = false;
      this.subscriptions.add(
        forkJoin({
          company: company$,
          profile: this.digitalCardService.getProfile(this.profileId),
          fieldToggles: fieldToggles$,
        }).subscribe({
          next: ({ company, profile, fieldToggles }) => {
            this.companyFontFamily =
              (company.brandFontFamily ?? "").trim() ||
              (company.brandFontFileName ?? "")
                .trim()
                .replace(/\.[^.]+$/, "") ||
              "";
            this.companyFontFileUrl =
              (company.brandFontFileUrl ?? "").trim() || null;
            this.fieldToggles = fieldToggles;
            this.profileEmail =
              typeof profile.email === "string" &&
              profile.email.trim().length > 0
                ? profile.email.trim()
                : null;
            this.patchProfileForm(profile);
            if (profile.settings?.bannerImageUrl) {
              this.bannerPreviewUrl = `${profile.settings.bannerImageUrl}?t=${Date.now()}`;
            }
            if (profile.settings?.bannerPosterUrl) {
              this.bannerPosterPreviewUrl = `${profile.settings.bannerPosterUrl}?t=${Date.now()}`;
            }
            this.bannerIsAnimated =
              profile.settings?.bannerIsAnimated === true ||
              Boolean(profile.settings?.bannerPosterUrl);
            if (this.bannerIsAnimated && profile.settings?.bannerImageUrl) {
              void this.attachFreezeFrames(profile.settings.bannerImageUrl);
            }
            this.goToCardUrl = profile.publicUrl ?? null;
            this.profileStatus = profile.status;
            this.profileCurrentRenderId = profile.currentRenderId ?? null;
            this.profileEmployee = profile.employee ?? null;
            this.loading = false;
            this.refreshPreviewUrl();
            this.syncDerivedEditorState();
          },
          error: () => {
            this.loading = false;
          },
        }),
      );
    } else {
      this.isCreateMode = true;
      this.subscriptions.add(
        forkJoin({
          company: company$,
          fieldToggles: fieldToggles$,
        }).subscribe({
          next: ({ company, fieldToggles }) => {
            this.companyFontFamily =
              (company.brandFontFamily ?? "").trim() ||
              (company.brandFontFileName ?? "")
                .trim()
                .replace(/\.[^.]+$/, "") ||
              "";
            this.companyFontFileUrl =
              (company.brandFontFileUrl ?? "").trim() || null;
            this.fieldToggles = fieldToggles;
            this.loading = false;
            this.refreshPreviewUrl();
            this.syncDerivedEditorState();
          },
          error: () => {
            this.loading = false;
            this.syncDerivedEditorState();
          },
        }),
      );
    }
  }

  get walletDraftGroup(): FormGroup<WalletDraftFormControls> {
    return this.form.get("walletDraft") as FormGroup<WalletDraftFormControls>;
  }

  private syncDerivedEditorState(): void {
    this.syncOrderedFieldToggles();
    this.previewSnapshot = {
      settings: this.buildSettingsPayload(),
      walletSettings: this.buildWalletPayload(),
      employee: this.buildPreviewEmployeePayload(),
    };
  }

  private syncOrderedFieldToggles(): void {
    const byKey = new Map(
      this.availableFieldToggles.map((toggle) => [toggle.key, toggle]),
    );
    const seen = new Set<string>();
    const ordered: CardFieldToggle[] = [];
    const fieldOrder = this.form.get("fieldOrder")?.value ?? [];
    for (const key of fieldOrder) {
      const toggle = byKey.get(key);
      if (toggle && !seen.has(toggle.key)) {
        ordered.push(toggle);
        seen.add(toggle.key);
      }
    }
    for (const toggle of this.availableFieldToggles) {
      if (!seen.has(toggle.key)) {
        ordered.push(toggle);
        seen.add(toggle.key);
      }
    }
    this.orderedFieldToggles = ordered;
  }

  private buildWalletPayload(): WalletSettingsData {
    const v = this.walletDraftGroup.getRawValue();
    const logoCustom = v.logoCustomUrl || null;
    const inheritWalletBg = v.inheritWalletBackgroundFromCard !== false;
    const bg =
      (v.appleBackgroundHex ?? "").trim() ||
      (v.googleBackgroundHex ?? "").trim() ||
      null;
    const inherit = v.inheritCardFieldVisibility !== false;
    const showEmail = v.walletShowEmail !== false;
    const showPhone = v.walletShowPhone !== false;
    const visibleFields = inherit
      ? null
      : {
          email: showEmail,
          phoneNumber: showPhone,
          mobileNumber: showPhone,
        };
    return {
      inheritWalletBackgroundFromCard: inheritWalletBg,
      inheritCardFieldVisibility: inherit,
      showAvatar: v.walletShowAvatar,
      showJobTitle: v.walletShowJobTitle,
      showContactList: showEmail || showPhone,
      showQr: true,
      showWalletPassCompanyName: v.walletShowPassCompanyName !== false,
      visibleFields,
      walletQrCaption: v.walletQrCaption || null,
      logoMode: v.logoMode,
      logoCustomUrl: logoCustom,
      appleBackgroundHex: inheritWalletBg ? null : bg,
      googleBackgroundHex: inheritWalletBg ? null : bg,
      appleForegroundRgb: null,
      googlePrimaryImage: v.googlePrimaryImage,
      googleRightColumn: v.googleRightColumn,
    };
  }

  private patchWalletSettings(ws: WalletSettingsData | null | undefined): void {
    const o = ws ?? {};
    const bgRaw =
      (typeof o.appleBackgroundHex === "string" &&
        o.appleBackgroundHex.trim()) ||
      (typeof o.googleBackgroundHex === "string" &&
        o.googleBackgroundHex.trim()) ||
      "";
    const bg = bgRaw ? this.normalizeHex(bgRaw) : "";
    const hasCustomBg = Boolean(bgRaw);
    const explicitInheritBg = o.inheritWalletBackgroundFromCard;
    const inheritWalletBg =
      explicitInheritBg === false
        ? false
        : explicitInheritBg === true
          ? true
          : !hasCustomBg;
    const inherit = o.inheritCardFieldVisibility !== false;
    const vf = o.visibleFields;
    const contactGate = o.showContactList !== false;
    const emailOn = !contactGate
      ? false
      : inherit || !vf || typeof vf !== "object"
        ? true
        : vf["email"] !== false;
    const phoneOn = !contactGate
      ? false
      : inherit || !vf || typeof vf !== "object"
        ? true
        : vf["phoneNumber"] !== false || vf["mobileNumber"] !== false;
    this.walletDraftGroup.patchValue({
      inheritWalletBackgroundFromCard: inheritWalletBg,
      logoMode:
        o.logoMode === "hidden" || o.logoMode === "custom"
          ? o.logoMode
          : "inherit",
      logoCustomUrl: typeof o.logoCustomUrl === "string" ? o.logoCustomUrl : "",
      appleBackgroundHex: inheritWalletBg ? "" : bg,
      googleBackgroundHex: inheritWalletBg ? "" : bg,
      inheritCardFieldVisibility: inherit,
      walletShowAvatar: o.showAvatar !== false,
      walletShowJobTitle: o.showJobTitle !== false,
      walletShowEmail: emailOn,
      walletShowPhone: phoneOn,
      walletShowPassCompanyName: o.showWalletPassCompanyName !== false,
      walletQrCaption:
        typeof o.walletQrCaption === "string" &&
        o.walletQrCaption.trim().length > 0
          ? o.walletQrCaption
          : DEFAULT_WALLET_QR_CAPTION,
      googlePrimaryImage: o.googlePrimaryImage ?? "employee_photo",
      googleRightColumn: o.googleRightColumn ?? "auto",
    });
  }

  private patchProfileForm(profile: {
    displayName?: string | null;
    settings?: DigitalCardSettingsData;
    walletSettings?: WalletSettingsData;
  }): void {
    const fontCssUrl = (profile.settings?.fontCssUrl ?? "").trim();
    const fontFamily = (profile.settings?.fontFamily ?? "").trim();
    const fontName = (profile.settings?.fontName ?? "").trim();
    const familyPrimary = (fontFamily.split(",")[0] ?? "")
      .trim()
      .replace(/^["']+|["']+$/g, "");
    let fontPresetId = "default";
    if (
      !fontCssUrl &&
      this.companyBrandFontReady &&
      familyPrimary.toLowerCase() === this.companyFontFamily.toLowerCase()
    ) {
      fontPresetId = BRAND_FONT_PRESET_ID;
    } else if (fontName) {
      const byName = this.fontPresets.find(
        (font) => font.label.trim().toLowerCase() === fontName.toLowerCase(),
      );
      if (byName) {
        fontPresetId = byName.id;
      }
    }
    if (fontPresetId === "default" && fontFamily) {
      const byFamily = this.fontPresets.find(
        (font) =>
          font.fontFamily.trim().toLowerCase() === fontFamily.toLowerCase(),
      );
      if (byFamily) {
        fontPresetId = byFamily.id;
      }
    }
    const profileLanguageRaw =
      typeof profile.settings?.profileLanguage === "string"
        ? profile.settings.profileLanguage
        : "";
    this.form.patchValue({
      displayName: profile.displayName ?? "",
      fontPreset: fontPresetId,
      bannerType: profile.settings?.bannerType ?? "gradient",
      bannerGradientStart: profile.settings?.bannerGradientStart ?? "#ffffff",
      bannerGradientEnd: profile.settings?.bannerGradientEnd ?? "#f5f5f0",
      brandColorPrimary: profile.settings?.brandColorPrimary ?? "#000000",
      brandColorSecondary: profile.settings?.brandColorSecondary ?? "",
      cardBackgroundColor: profile.settings?.cardBackgroundColor ?? "#ffffff",
      pageBackgroundColor: profile.settings?.pageBackgroundColor ?? "#ffffff",
      showBanner: profile.settings?.showBanner ?? true,
      showAvatar: profile.settings?.showAvatar ?? true,
      showJobTitle: profile.settings?.showJobTitle ?? true,
      showQr: profile.settings?.showQr ?? true,
      showCompanyLogo: profile.settings?.showCompanyLogo !== false,
      showAboutMe: profile.settings?.showAboutMe ?? true,
      showPronounceName:
        this.namePronunciationEnabled &&
        profile.settings?.showPronounceName !== false,
      showSaveContact: profile.settings?.showSaveContact ?? true,
      showBookMeeting: profile.settings?.showBookMeeting ?? true,
      visibleFields: this.deriveVisibleFields(profile.settings),
      fieldOrder: this.deriveFieldOrder(profile.settings),
      companyLogoUrl:
        typeof profile.settings?.companyLogoUrl === "string" &&
        profile.settings.companyLogoUrl.trim().length > 0
          ? profile.settings.companyLogoUrl.trim()
          : "",
      customHtml:
        typeof profile.settings?.customHtml === "string"
          ? profile.settings.customHtml
          : "",
      profileLanguage:
        profileLanguageRaw &&
        (PROFILE_LANGUAGES as readonly string[]).includes(profileLanguageRaw)
          ? profileLanguageRaw
          : "",
    });
    this.patchWalletSettings(profile.walletSettings);
  }

  private deriveVisibleFields(
    settings: DigitalCardSettingsData | null | undefined,
  ): Record<string, boolean> {
    if (settings?.visibleFields && typeof settings.visibleFields === "object") {
      return { ...settings.visibleFields };
    }
    const legacyContact = settings?.showContactList ?? true;
    const legacyLinks = settings?.showCustomLinks ?? true;
    if (legacyContact && legacyLinks) {
      return {};
    }
    const map: Record<string, boolean> = {};
    if (!legacyContact) {
      map["phoneNumber"] = false;
      map["mobileNumber"] = false;
      map["email"] = false;
    }
    if (!legacyLinks) {
      map["customLinks"] = false;
      for (const toggle of this.fieldToggles) {
        if (
          toggle.key !== "phoneNumber" &&
          toggle.key !== "mobileNumber" &&
          toggle.key !== "email" &&
          toggle.key !== "customLinks"
        ) {
          map[toggle.key] = false;
        }
      }
    }
    return map;
  }

  private deriveFieldOrder(
    settings: DigitalCardSettingsData | null | undefined,
  ): string[] {
    return Array.isArray(settings?.fieldOrder)
      ? settings.fieldOrder
          .map((key) => (typeof key === "string" ? key.trim() : ""))
          .filter((key): key is string => key.length > 0)
      : [];
  }

  private branchVariablesToEmployeeSettings(
    branches: ReadonlyArray<Branch>,
  ): EmployeeSetting[] {
    const rows = new Map<string, EmployeeSetting>();

    for (const branch of branches) {
      for (const [key, variable] of Object.entries(branch.variables ?? {})) {
        const name = variable?.name?.trim() || key.trim();
        if (!name || rows.has(name)) continue;
        rows.set(name, {
          domain: branch.domain,
          name,
          label: variable.label,
          defaultValue: variable.defaultValue ?? null,
          iconUrl: variable.iconUrl ?? null,
          overridable: variable.overridable,
          mandatory: variable.mandatory,
        });
      }
    }

    return [...rows.values()];
  }

  private get availableFieldToggles(): CardFieldToggle[] {
    return [...this.fieldToggles, ...this.customLinkFieldToggles];
  }

  private get customLinkFieldToggles(): CardFieldToggle[] {
    const employee = this.previewEmployeeData ?? this.profileEmployee;
    return (employee?.customLinks ?? []).flatMap((link, index) => {
      const label = link?.text?.trim() || link?.url?.trim();
      if (!label) return [];
      return [{ key: customLinkFieldKey(index), label }];
    });
  }

  isFieldVisible(key: string): boolean {
    const map = this.form.get("visibleFields")?.value ?? {};
    if (isCustomLinkFieldKey(key) && map["customLinks"] === false) {
      return false;
    }
    return map[key] !== false;
  }

  toggleFieldVisibility(key: string, value: boolean): void {
    const ctrl = this.form.get("visibleFields");
    if (!ctrl) return;
    const next = { ...(ctrl.value ?? {}) };
    if (value) {
      delete next[key];
    } else {
      next[key] = false;
    }
    ctrl.setValue(next);
    ctrl.markAsDirty();
  }

  dropContactField(event: CdkDragDrop<CardFieldToggle[]>): void {
    const next = [...this.orderedFieldToggles];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    const ctrl = this.form.get("fieldOrder");
    ctrl?.setValue(next.map((toggle) => toggle.key));
    ctrl?.markAsDirty();
  }

  private refreshPreviewUrl(): void {
    const base = getDigitalCardBaseUrl();
    this.previewQrData = base ? `${base}/preview` : "";
  }

  private initPreviewEmployeeStream(): void {
    this.subscriptions.add(
      this.previewEmail$
        .pipe(
          distinctUntilChanged(),
          tap(() => {
            if (this.pageReady) {
              this.previewEmployeeLoading = true;
            }
          }),
          switchMap((email) =>
            email
              ? this.digitalCardService.getPreviewEmployee(email).pipe(
                  map((r) => r.employee),
                  catchError(() => of<EmployeeWithBrand | null>(null)),
                )
              : of<EmployeeWithBrand | null>(null),
          ),
        )
        .subscribe((employee) => {
          this.applyPreviewEmployeeData(employee);
          this.previewEmployeeLoading = false;
        }),
    );
  }

  private bootstrapPreviewEmployee(): void {
    this.subscriptions.add(
      this.employeesService
        .getEmployeesPicker({ pager: { page: 0, limit: 1 } })
        .pipe(
          map((res) => res?.data?.[0] ?? null),
          catchError(() => of<EmployeePickerItem | null>(null)),
          take(1),
        )
        .subscribe((employee) => {
          this.previewEmployee = employee;
          const email = this.previewEmployee?.email?.trim().toLowerCase() ?? "";
          if (email) {
            this.previewEmail$.next(email);
          } else {
            this.applyPreviewEmployeeData(null);
          }
        }),
    );
  }

  get activeTemplateId(): string | null {
    const id = !this.isCreateMode ? (this.profileId?.trim() ?? "") : "";
    return id.length > 0 ? id : null;
  }

  get previewIframeUrl(): string {
    return this.previewQrData;
  }

  get previewSurface(): "card" | "wallet" {
    if (!this.walletTabEnabled) {
      return "card";
    }
    return this.editorTabIndex === 1 ? "wallet" : "card";
  }

  onEditorTabIndexChange(index: number): void {
    this.editorTabIndex = index;
  }

  onPreviewEmployeeChange(emp: EmployeePickerItem | null): void {
    this.previewEmployee = emp;
    this.previewEmail$.next(emp?.email?.trim().toLowerCase() ?? "");
  }

  private applyPreviewEmployeeData(data: EmployeeWithBrand | null): void {
    this.previewEmployeeData = data;
    this.previewSettled = true;
    this.syncDerivedEditorState();
  }

  get displayName(): boolean {
    return false;
  }

  get pageReady(): boolean {
    return !this.loading && this.previewSettled;
  }

  get canSave(): boolean {
    return true;
  }

  private buildPreviewEmployeePayload(): EmployeeWithBrand | null {
    const employee = this.previewEmployeeData ?? this.profileEmployee ?? null;
    if (!employee) {
      if (!this.companyBrandFontReady) {
        return null;
      }
      return {
        companyData: {
          brandFontFamily: this.companyFontFamily || null,
          brandFontFileUrl: this.companyFontFileUrl,
        },
      } as EmployeeWithBrand;
    }
    const profileLanguage = this.form.get("profileLanguage")?.value;
    const localized =
      profileLanguage &&
      (PROFILE_LANGUAGES as readonly string[]).includes(profileLanguage)
        ? employee.localizedProfiles?.[profileLanguage as ProfileLanguage]
        : undefined;
    return {
      ...employee,
      givenName: localized?.givenName || employee.givenName,
      familyName: localized?.familyName || employee.familyName,
      fullName: localized?.fullName || employee.fullName,
      jobTitle: localized?.jobTitle || employee.jobTitle,
      companyData: {
        ...(employee.companyData ?? {}),
        brandFontFamily: this.companyFontFamily || null,
        brandFontFileUrl: this.companyFontFileUrl,
      },
    };
  }

  private get displayNameValue(): string | undefined {
    return this.form.get("displayName")?.value?.trim() || undefined;
  }

  private buildSettingsPayload(): DigitalCardSettingsData {
    const v = this.form.getRawValue();
    const selectedFont =
      this.fontPresets.find((font) => font.id === v.fontPreset) ??
      this.fontPresets[0];
    const vf = v.visibleFields ?? {};
    const showContactList = (
      ["phoneNumber", "mobileNumber", "email"] as const
    ).some((k) => vf[k] !== false);
    const showCustomLinks = this.orderedFieldToggles.some(
      (toggle) =>
        toggle.key !== "phoneNumber" &&
        toggle.key !== "mobileNumber" &&
        toggle.key !== "email" &&
        this.isFieldVisible(toggle.key),
    );
    const isImage = v.bannerType === "image";
    const bannerUrl =
      isImage && this.bannerPreviewUrl
        ? this.bannerPreviewUrl.replace(/\?t=\d+$/, "")
        : null;
    const bannerPosterUrl =
      isImage && this.bannerPosterPreviewUrl
        ? this.bannerPosterPreviewUrl.replace(/\?t=\d+$/, "")
        : null;
    const profileLanguage =
      v.profileLanguage &&
      (PROFILE_LANGUAGES as readonly string[]).includes(v.profileLanguage)
        ? v.profileLanguage
        : null;
    return {
      bannerType: v.bannerType,
      bannerGradientStart: v.bannerGradientStart || undefined,
      bannerGradientEnd: v.bannerGradientEnd || undefined,
      bannerImageUrl: isImage ? (bannerUrl ?? undefined) : null,
      bannerPosterUrl: isImage ? (bannerPosterUrl ?? null) : null,
      bannerIsAnimated: isImage ? this.bannerIsAnimated : false,
      brandColorPrimary: v.brandColorPrimary || undefined,
      brandColorSecondary: v.brandColorSecondary || null,
      cardBackgroundColor: v.cardBackgroundColor || undefined,
      pageBackgroundColor: v.pageBackgroundColor || undefined,
      showBanner: v.showBanner,
      showAvatar: v.showAvatar,
      showJobTitle: v.showJobTitle,
      showQr: v.showQr,
      showCompanyLogo: v.showCompanyLogo !== false,
      showAboutMe: v.showAboutMe,
      showPronounceName: this.namePronunciationEnabled && !!v.showPronounceName,
      showContactList,
      showCustomLinks,
      showSaveContact: v.showSaveContact,
      showBookMeeting: v.showBookMeeting,
      visibleFields: v.visibleFields,
      fieldOrder: this.orderedFieldToggles.map((toggle) => toggle.key),
      profileLanguage,
      companyLogoUrl: v.companyLogoUrl || null,
      fontName: selectedFont.label,
      fontFamily: selectedFont.fontFamily,
      fontCssUrl:
        selectedFont.id === BRAND_FONT_PRESET_ID
          ? null
          : (selectedFont.fontCssUrl ?? null),
      customHtml:
        typeof v.customHtml === "string" && v.customHtml.trim().length > 0
          ? v.customHtml.trim()
          : undefined,
    };
  }

  private handleSaveSuccess(updated: {
    publicUrl?: string;
    status?: string;
    currentRenderId?: string | null;
  }): void {
    this.goToCardUrl = updated?.publicUrl ?? null;
    this.profileStatus = updated?.status;
    this.profileCurrentRenderId = updated?.currentRenderId ?? null;
    this.refreshPreviewUrl();
    this.toastr.success(
      this.translateService.instant("digitalCard.settings.saved") as string,
    );
    this.store.dispatch(
      new GetDigitalCardProfilesAction({
        templatesOnly: true,
        mine: false,
      }),
    );
    void this.router.navigate(["/digital-cards"]);
  }

  private doDeploy(
    profileId: string,
    email: string,
    hash: string,
    employee: EmployeeWithBrand,
  ): void {
    this.digitalCardService
      .updateProfile(profileId, {
        settings: this.buildSettingsPayload(),
        walletSettings: this.buildWalletPayload(),
        displayName: this.displayNameValue,
        employee: {
          ...employee,
          companyData: {
            ...(employee.companyData ?? {}),
            brandFontFamily: this.companyFontFamily || null,
            brandFontFileUrl: this.companyFontFileUrl,
          },
        },
        hash,
        email,
      })
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (updated) => this.handleSaveSuccess(updated),
      });
  }

  save(): void {
    if (this.saving) {
      return;
    }
    if (!this.displayNameValue) {
      this.showNameInputDialog();
      return;
    }
    this.performSave();
  }

  private showNameInputDialog(): void {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translateService.instant(
          "digitalCards.enterName",
        ) as string,
        placeholder: this.translateService.instant("footers.name") as string,
        currentName: this.form.get("displayName")?.value || "",
        actionType: "save" as const,
        saveButtonText: this.translateService.instant("save") as string,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (!result?.name?.trim()) {
          return;
        }
        this.form.get("displayName")?.setValue(result.name.trim());
        this.performSave();
      });
  }

  private performSave(): void {
    this.errorStateMatcher.submitAttempted = true;
    if (this.saving) {
      return;
    }
    this.saving = true;
    if (this.isCreateMode) {
      this.saveCreateProfile();
      return;
    }
    if (!this.profileId) {
      this.saving = false;
      return;
    }
    this.saveExistingProfile(this.profileId, this.profileEmail);
  }

  private saveCreateProfile(): void {
    this.digitalCardService
      .createProfile(this.displayNameValue)
      .pipe(
        switchMap((created) =>
          this.digitalCardService.updateProfile(created.profileId, {
            settings: this.buildSettingsPayload(),
            walletSettings: this.buildWalletPayload(),
            displayName: this.displayNameValue,
          }),
        ),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (updated) => this.handleSaveSuccess(updated),
      });
  }

  private saveExistingProfile(
    profileId: string,
    profileEmail: string | null,
  ): void {
    if (!profileEmail) {
      this.digitalCardService
        .updateProfile(profileId, {
          settings: this.buildSettingsPayload(),
          walletSettings: this.buildWalletPayload(),
          displayName: this.displayNameValue,
        })
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: (updated) => this.handleSaveSuccess(updated),
        });
      return;
    }
    this.digitalCardService.getCurrentEmployee().subscribe({
      next: (res) => {
        const employee = res.employee;
        const email =
          typeof employee.email === "string" && employee.email.trim().length > 0
            ? employee.email.trim()
            : profileEmail;
        void generateEmployeeHash(email)
          .then((hash) => {
            this.doDeploy(profileId, email, hash, employee);
          })
          .catch(() => {
            this.saving = false;
          });
      },
      error: () => (this.saving = false),
    });
  }

  ngOnDestroy(): void {
    this.clearFreezeFrames();
    this.freezeFramePosterUpload$.complete();
    this.previewEmail$.complete();
    this.subscriptions.unsubscribe();
  }

  back(): void {
    void this.router.navigate(["/digital-cards"]);
  }

  get showFreezeFrame(): boolean {
    return (
      this.form.get("bannerType")?.value === "image" &&
      (this.bannerIsAnimated || this.freezeFrameLoading) &&
      Boolean(
        this.freezeFrameLoading ||
        this.freezeFramePreviewUrl ||
        this.bannerPosterPreviewUrl,
      )
    );
  }

  get showFreezeFrameSlider(): boolean {
    return (
      !this.freezeFrameLoading &&
      this.showFreezeFrame &&
      this.freezeFrameCount > 1 &&
      this.freezeFrameSource != null
    );
  }

  onFreezeFrameIndexInput(raw: string | number): void {
    const index = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(index) || !this.freezeFrameSource) return;
    const i = Math.min(
      Math.max(0, Math.floor(index)),
      this.freezeFrameCount - 1,
    );
    const nextUrl = this.freezeFrameSource.frameUrls[i];
    if (!nextUrl) return;
    this.freezeFrameIndex = i;
    this.freezeFramePreviewUrl = nextUrl;
    this.freezeFramePosterUpload$.next(i);
  }

  uploadBanner = (file: File) => {
    this.clearFreezeFrames();
    this.bannerPosterPreviewUrl = undefined;
    this.bannerIsAnimated = false;
    return this.digitalCardService.uploadCardBanner(file).pipe(
      map((res) => ({
        previewUrl: res.bannerImageUrl,
        uploadData: res,
      })),
    );
  };

  onBannerFileChange(image: Image): void {
    if (!image?.file || !image.uploadData) return;
    const res = image.uploadData as {
      bannerImageUrl: string;
      bannerPosterUrl: string | null;
      bannerIsAnimated: boolean;
    };
    this.bannerPreviewUrl = image.previewUrl;
    this.bannerPosterPreviewUrl = res.bannerPosterUrl
      ? `${res.bannerPosterUrl}?t=${Date.now()}`
      : undefined;
    this.bannerIsAnimated =
      res.bannerIsAnimated === true || Boolean(res.bannerPosterUrl);
    this.form.patchValue({ bannerType: "image" });
    this.freezeFrameLoading = this.bannerIsAnimated;
    if (this.bannerIsAnimated) {
      void this.attachFreezeFrames(image.file);
    }
  }

  removeBanner(): void {
    this.clearFreezeFrames();
    this.bannerPreviewUrl = undefined;
    this.bannerPosterPreviewUrl = undefined;
    this.bannerIsAnimated = false;
    this.syncDerivedEditorState();
  }

  private clearFreezeFrames(): void {
    this.freezeFrameToken += 1;
    this.freezeFrameSource?.dispose();
    this.freezeFrameSource = null;
    this.freezeFrameCount = 0;
    this.freezeFrameIndex = 0;
    this.freezeFramePreviewUrl = undefined;
    this.freezeFrameLoading = false;
  }

  private async attachFreezeFrames(input: File | string): Promise<void> {
    const token = ++this.freezeFrameToken;
    if (this.bannerIsAnimated) {
      this.freezeFrameLoading = true;
    }
    try {
      const source =
        typeof input === "string"
          ? await openFreezeFrameSourceFromUrl(input)
          : await openFreezeFrameSource(input);
      if (token !== this.freezeFrameToken) {
        source?.dispose();
        return;
      }
      if (!source) {
        this.freezeFrameLoading = false;
        return;
      }
      if (!source.animated && !this.bannerIsAnimated) {
        source.dispose();
        this.freezeFrameLoading = false;
        return;
      }
      this.freezeFrameSource = source;
      this.freezeFrameCount = source.frameCount;
      this.freezeFrameIndex = 0;
      this.freezeFramePreviewUrl = source.frameUrls[0];
      this.bannerIsAnimated = true;
      this.freezeFrameLoading = false;
      this.freezeFramePosterUpload$.next(0);
      this.syncDerivedEditorState();
    } catch {
      if (token === this.freezeFrameToken) {
        this.freezeFrameLoading = false;
      }
    }
  }

  private async uploadFreezeFramePoster(index: number): Promise<void> {
    const source = this.freezeFrameSource;
    if (!source?.frameUrls[index]) return;
    const token = this.freezeFrameToken;
    try {
      const res = await fetch(source.frameUrls[index]);
      const png = await res.blob();
      if (token !== this.freezeFrameToken) return;
      const file = new File([png], `banner-freeze-frame-${index}.png`, {
        type: "image/png",
      });
      this.digitalCardService.uploadCardBannerPoster(file).subscribe({
        next: (uploaded) => {
          if (token !== this.freezeFrameToken) return;
          this.bannerPosterPreviewUrl = `${uploaded.bannerPosterUrl}?t=${Date.now()}`;
          this.syncDerivedEditorState();
        },
      });
    } catch {
      // keep previous poster
    }
  }

  orphanCompanyLogoUrl(): string | null {
    const v = this.form.get("companyLogoUrl")?.value?.trim();
    if (!v) {
      return null;
    }
    return this.mediaImages.some((x) => x.imageUrl === v) ? null : v;
  }

  companyLogoSelectOptions(): MediaImage[] {
    const orphan = this.orphanCompanyLogoUrl();
    if (!orphan) {
      return this.mediaImages;
    }
    return [
      {
        imageId: `orphan:${orphan}`,
        name: this.translateService.instant(
          "digitalCards.companyLogoStoredNotInMedia",
        ) as string,
        imageUrl: orphan,
      },
      ...this.mediaImages,
    ];
  }

  walletLogoSelectOptions(): MediaImage[] {
    const orphan = this.orphanWalletLogoUrl();
    if (!orphan) {
      return this.mediaImages;
    }
    return [
      {
        imageId: `orphan-wallet:${orphan}`,
        name: this.translateService.instant(
          "digitalCards.companyLogoStoredNotInMedia",
        ) as string,
        imageUrl: orphan,
      },
      ...this.mediaImages,
    ];
  }

  orphanWalletLogoUrl(): string | null {
    const v = this.walletDraftGroup.get("logoCustomUrl")?.value?.trim();
    if (!v) {
      return null;
    }
    return this.mediaImages.some((x) => x.imageUrl === v) ? null : v;
  }

  walletLogoUnifiedValue(): string {
    const g = this.walletDraftGroup;
    const mode = g.get("logoMode")?.value;
    const url = (g.get("logoCustomUrl")?.value ?? "").trim();
    if (mode === "custom" && url.length > 0) {
      return url;
    }
    return this.walletLogoSelInherit;
  }

  onWalletLogoUnifiedChange(value: unknown): void {
    const sel = typeof value === "string" ? value : "";
    if (sel === this.walletLogoSelInherit) {
      this.walletDraftGroup.patchValue({
        logoMode: "inherit",
        logoCustomUrl: "",
      });
    } else if (sel.length > 0) {
      this.walletDraftGroup.patchValue({
        logoMode: "custom",
        logoCustomUrl: sel,
      });
    }
    this.walletDraftGroup.get("logoMode")?.markAsDirty();
    this.walletDraftGroup.get("logoCustomUrl")?.markAsDirty();
  }

  bannerOptionsVisible(): boolean {
    return this.form.get("showBanner")?.value === true;
  }

  companyLogoOptionsVisible(): boolean {
    return this.form.get("showCompanyLogo")?.value === true;
  }

  walletBackgroundOptionsVisible(): boolean {
    return (
      this.walletDraftGroup.get("inheritWalletBackgroundFromCard")?.value ===
      false
    );
  }

  walletFieldOverridesVisible(): boolean {
    return (
      this.walletDraftGroup.get("inheritCardFieldVisibility")?.value === false
    );
  }

  private loadMediaImages(): void {
    this.mediaImagesLoading = true;
    this.imagesService.getImages().subscribe({
      next: (list) => {
        this.mediaImages = Array.isArray(list) ? list : [];
        this.mediaImagesLoading = false;
      },
      error: () => {
        this.mediaImages = [];
        this.mediaImagesLoading = false;
      },
    });
  }

  private normalizeHex(v: string): string {
    let s = (v || "").trim().toLowerCase();
    if (!s.startsWith("#")) s = "#" + s;
    if (s.length === 4) s = "#" + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
    return s.length === 7 ? s : "#000000";
  }

  private isValidHex(v: string): boolean {
    const s = v.startsWith("#") ? v.slice(1) : v;
    return /^[0-9a-fA-F]{3}$/.test(s) || /^[0-9a-fA-F]{6}$/.test(s);
  }

  onFormColorChange(
    controlName: keyof Pick<
      DigitalCardFormControls,
      | "bannerGradientStart"
      | "bannerGradientEnd"
      | "brandColorPrimary"
      | "cardBackgroundColor"
      | "pageBackgroundColor"
    >,
    value: string,
  ): void {
    this.form.patchValue({ [controlName]: value });
  }

  walletUnifiedBgPickerValue(): string {
    const raw =
      this.walletDraftGroup.get("appleBackgroundHex")?.value?.trim() ?? "";
    if (raw && this.isValidHex(raw)) {
      return this.normalizeHex(raw);
    }
    const brand = (this.form.get("brandColorPrimary")?.value ?? "")
      .toString()
      .trim();
    if (brand && this.isValidHex(brand)) {
      return this.normalizeHex(brand);
    }
    return "#000000";
  }

  onWalletInheritBackgroundToggle(checked: boolean): void {
    const g = this.walletDraftGroup;
    if (checked) {
      g.patchValue({
        appleBackgroundHex: "",
        googleBackgroundHex: "",
      });
    } else {
      const seed = this.walletUnifiedBgPickerValue();
      g.patchValue({
        appleBackgroundHex: seed,
        googleBackgroundHex: seed,
      });
    }
    g.get("inheritWalletBackgroundFromCard")?.markAsDirty();
    g.get("appleBackgroundHex")?.markAsDirty();
    g.get("googleBackgroundHex")?.markAsDirty();
  }

  onWalletUnifiedBgChange(value: string): void {
    this.walletDraftGroup.patchValue({
      appleBackgroundHex: value,
      googleBackgroundHex: value,
    });
    this.walletDraftGroup.get("appleBackgroundHex")?.markAsDirty();
    this.walletDraftGroup.get("googleBackgroundHex")?.markAsDirty();
  }

  walletPassLogoEnabled(): boolean {
    return this.walletDraftGroup.get("logoMode")?.value !== "hidden";
  }

  onWalletPassLogoToggle(checked: boolean): void {
    if (checked) {
      this.walletDraftGroup.patchValue({
        logoMode: "inherit",
        logoCustomUrl: "",
      });
    } else {
      this.walletDraftGroup.patchValue({
        logoMode: "hidden",
        logoCustomUrl: "",
      });
    }
    this.walletDraftGroup.get("logoMode")?.markAsDirty();
    this.walletDraftGroup.get("logoCustomUrl")?.markAsDirty();
  }
}
